import { FC } from 'react';

import { CallOffOrderDetail } from '@ravago/shared/page-data/models/elements';
import { Avatar } from '@ravago/shared/radiance/components/avatar/components/Avatar/Avatar';
import { Box } from '@ravago/shared/radiance/components/box/components/Box/Box';
import { Table } from '@ravago/shared/radiance/components/table/components/Table/Table';
import { TableBody } from '@ravago/shared/radiance/components/table/components/TableBody/TableBody';
import { TableCell } from '@ravago/shared/radiance/components/table/components/TableCell/TableCell';
import { TableRow } from '@ravago/shared/radiance/components/table/components/TableRow/TableRow';
import { Typography } from '@ravago/shared/radiance/components/typography/components/Typography/Typography';
import { useFormatter, useTranslations } from '@ravago/shared/translations/client';

import { DynamicProductBlockUtils } from '../../../../utils/dynamic-product-block/dynamic-product-block.utils';
import { OrderPriceInfoRow } from '../../../shared/order/order-price-info-row';
import { DynamicPageLink } from '../../dynamic-page-link/dynamic-page-link';

interface Props {
   locale: string;
   callOffOrderDetail: CallOffOrderDetail;
   productsBaseRoute: string | null;
   usesAgreementApp: boolean;
   canViewPrices: boolean;
}

export const CallOffOrderInfo: FC<Props> = ({ locale, callOffOrderDetail, productsBaseRoute, usesAgreementApp, canViewPrices }) => {
   const translate = useTranslations();
   const format = useFormatter();

   return (
      <Box direction="column">
         <Box items="center">
            {callOffOrderDetail.contentfulProduct && (
               <Avatar size="large" shape="square" src={callOffOrderDetail.contentfulProduct?.brand?.producer?.logo?.url} />
            )}
            <Box direction="column" gap="none" spacing={{ left: callOffOrderDetail.contentfulProduct ? 'none' : 'md' }}>
               {callOffOrderDetail.contentfulProduct && productsBaseRoute ? (
                  <DynamicPageLink
                     id={`dynamic-link__call-off-order__${callOffOrderDetail.contentfulProduct.slug}`}
                     locale={locale}
                     baseRoute={{
                        url: productsBaseRoute,
                        noFollow: false
                     }}
                     slug={DynamicProductBlockUtils.getProductSlugWithParams(
                        callOffOrderDetail.contentfulProduct.slug,
                        callOffOrderDetail.product.color,
                        callOffOrderDetail.product.producerColor,
                        'call-off'
                     )}
                  >
                     <Typography component="span" variant="body-highlight" color="primary">
                        {callOffOrderDetail.product.description}
                     </Typography>
                  </DynamicPageLink>
               ) : (
                  <Typography component="span" variant="body-highlight" color="strong">
                     {callOffOrderDetail.product.description}
                  </Typography>
               )}
               {callOffOrderDetail.customerProductReferences.length > 0 && (
                  <Typography component="span" variant="small" color="subtle">
                     {callOffOrderDetail.customerProductReferences.join(' • ')}
                  </Typography>
               )}
            </Box>
         </Box>
         <Table tableLayout="fixed" stroked="horizontal" rowDividers={false} hover={false}>
            <TableBody>
               <TableRow>
                  <TableCell>
                     <Typography component="b" variant="small-highlight" color="primary">
                        {translate('my-orders.order-reference')}
                     </Typography>
                  </TableCell>
                  <TableCell>{callOffOrderDetail.customerOrderReferences.join(' • ') || '-'}</TableCell>
               </TableRow>
               <TableRow>
                  <TableCell>
                     <Typography component="b" variant="small-highlight" color="primary">
                        {usesAgreementApp ? translate('my-orders.total-quantity') : translate('my-orders.remaining-quantity')}
                     </Typography>
                  </TableCell>
                  <TableCell>
                     <Typography variant="small">{format.quantity(callOffOrderDetail.totalQuantity)}</Typography>
                  </TableCell>
               </TableRow>
               <TableRow>
                  <TableCell>
                     <Typography component="b" variant="small-highlight" color="primary">
                        {translate('my-orders.packaging')}
                     </Typography>
                  </TableCell>
                  <TableCell>
                     <Typography variant="small">{callOffOrderDetail.packagings.join(' • ')}</Typography>
                  </TableCell>
               </TableRow>
               <OrderPriceInfoRow
                  unitPrice={callOffOrderDetail.unitPrice}
                  isUnitPriceFixed={callOffOrderDetail.isUnitPriceFixed}
                  canViewPrices={canViewPrices}
               />
               <TableRow>
                  <TableCell>
                     <Typography component="b" variant="small-highlight" color="primary">
                        {translate('my-orders.comment')}
                     </Typography>
                  </TableCell>
                  <TableCell>
                     <Typography variant="small">{callOffOrderDetail.orderConfirmationTexts.join(' • ') || '-'}</Typography>
                  </TableCell>
               </TableRow>
            </TableBody>
         </Table>
      </Box>
   );
};
